'use client';

import { useState, useEffect, useRef } from "react";

/**
 * Dynamic personalization visual — a 3-beat loop that conveys the whole
 * section: (1) your templates, (2) the right one auto-matched, (3) the report
 * written in your phrasing & structure.
 * Reuses the .dxrcap-spot-viz / .viz-* styles defined in DxrCapabilities.
 */

const DICTATION = "Chronic small-vessel ischemic changes…";
const WORDS = DICTATION.split(" ");
const WORD_MS = 58;
const TEMPLATES = ["CT Chest", "MRI Brain", "USG Abdomen", "Screening Mammo"];
const MATCH_INDEX = 1;

export default function TemplateMatchViz() {
  const [shown, setShown] = useState(0);
  const [phase, setPhase] = useState("dictate"); // dictate → match → report → loop
  const [active, setActive] = useState(false);   // on-screen & motion-allowed
  const ref = useRef(null);

  // Only run the loop while on-screen; honor reduced-motion by showing the end state.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setPhase("report"); setShown(WORDS.length); return; }
    const io = new IntersectionObserver(
      (entries) => setActive(entries[0].isIntersecting),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Stream the dictation, then advance to "match".
  useEffect(() => {
    if (!active || phase !== "dictate") return;
    if (shown < WORDS.length) {
      const t = setTimeout(() => setShown(shown + 1), WORD_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase("match"), 700);
    return () => clearTimeout(t);
  }, [shown, phase, active]);

  // Hold each later phase, then move on / loop.
  useEffect(() => {
    if (!active) return;
    if (phase === "match") {
      const t = setTimeout(() => setPhase("report"), 1500);
      return () => clearTimeout(t);
    }
    if (phase === "report") {
      const t = setTimeout(() => { setPhase("dictate"); setShown(0); }, 3400);
      return () => clearTimeout(t);
    }
  }, [phase, active]);

  const streaming = phase === "dictate" && shown < WORDS.length;
  const dictWords = phase === "dictate" ? shown : WORDS.length;
  const arrowLabel =
    phase === "report" ? "Written in your phrasing & structure"
    : phase === "match" ? "Matched from your templates"
    : streaming ? "Reading dictation…"
    : "Matching template…";

  return (
    <div className="dxrcap-spot-viz" aria-hidden="true" ref={ref}>
      <span className="viz-brand">NATOE <b>DXR</b></span>

      <div className="viz-card viz-dict">
        <span className="viz-label">Dictation</span>
        <p suppressHydrationWarning>
          “{WORDS.slice(0, dictWords).join(" ")}{streaming && <span className="viz-caret" />}”
        </p>
      </div>

      <div className="viz-arrow">
        <span>{arrowLabel}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#2997FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v16m0 0l-6-6m6 6l6-6" /></svg>
      </div>

      {phase === "report" ? (
        <div className="viz-report">
          <span className="viz-report-chip">MRI Brain · your house style</span>
          <p><span className="lbl">Findings:</span></p>
          <p className="viz-rep-line"><span className="lbl">White Matter:</span> <span className="hl">Chronic small-vessel ischaemic changes.</span></p>
          <p className="viz-rep-line"><span className="lbl">Parenchyma:</span> <span className="b">No acute infarct or haemorrhage.</span></p>
          <p className="viz-rep-line"><span className="lbl">Ventricles & Sulci:</span> <span className="b">Age-appropriate.</span></p>
          <p className="viz-rep-line"><span className="lbl">Posterior Fossa:</span> <span className="b">Unremarkable.</span></p>
          <p><span className="lbl">Impression:</span> <span className="hl">Small-vessel ischaemic change.</span> <span className="b">No acute abnormality.</span></p>
        </div>
      ) : (
        <div className="viz-templates">
          <span className="viz-sub">Your templates</span>
          {TEMPLATES.map((t, i) => {
            const isMatched = phase === "match" && i === MATCH_INDEX;
            return (
              <div key={t} className={`viz-tpl ${isMatched ? "is-matched" : ""}`}>
                <span className="viz-tpl-dot" />
                {t}
                {isMatched && <span className="viz-tpl-tag">matched</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
